import { useState } from "react";
import useSWR from "swr";
import { AdditionalUserData } from "../../../globals/interfaces";
import apiClient from "../../../apiClient";
import useUser from "../../../customHooks/useUser";
import styled from "styled-components";
import ActionIcon from "../../../components/custom/ActionIcon";
import AddToSchemaModal from "./AddToSchemaModal";
import { safeGet } from "../../../utils";
import _ from "lodash";
import useConfirmation from "../../../components/ConfirmationModal/useConfirmation";
import useLoading from "../../../customHooks/useLoading";

export type AddToSchema = (labelName: string, schemaName: string) => Promise<void>;

const fetcher = async (key: string): Promise<AdditionalUserData> => {
    try {
        const res = await apiClient.get(key);
        return res.data || {}
    } catch (err) {
        alert(err);
        return {};
    }
}

export default function AdditionalEmployeeData () {
    const { user } = useUser();
    const { s_unit } = user;
    const {
        data,
        mutate
    } = useSWR(`/additional-user-data/schema/${s_unit}`, fetcher, {
        fallbackData: {}
    });
    const { trigger } = useConfirmation();
    const { setLoading } = useLoading();
    const [modal, setModal] = useState(false);

    const addToSchema: AddToSchema = async (labelName: string, schemaName: string) => {
        try {
            setLoading(true);
            const res = await apiClient.post(`/additional-user-data/schema`, {
                schemaName,
                labelName,
                unit: s_unit
            });
            mutate();
            setModal(false);
        } catch (err) {
            alert(err);
        } finally {
            setModal(false);
            setLoading(false);
        }
    }

    const removeFromSchema = async (schemaName: string) => {
        try {
            setLoading(true);
            const res = await apiClient.delete(`/additional-user-data/schema?schemaName=${schemaName}&unit=${s_unit}`);
            mutate();
        } catch (err) {
            alert(err);
        } finally {
            setModal(false);
            setLoading(false);
        }
    }

    const handleDeleteTrigger = (schemaName: string, labelName: string) => {
        trigger(`Are you sure you want to delete this field: ${labelName}`, () => removeFromSchema(schemaName));
    }

    return (
        <div>
            <FlexContainer>
                <h6>Additional Employee Data Fields for {user.s_unit}</h6>
                <ActionIcon  
                    type={'add'}
                    onClick={() => setModal(true)}
                />
                <AddToSchemaModal 
                    modal={modal}
                    setModal={setModal}
                    data={data}
                    addToSchema={addToSchema}
                />
            </FlexContainer>
            <ol>
                {
                    Object.keys(data).map(key => (
                        <CustomListItem>{data[key]} 
                            <ActionIcon 
                                type={'delete'} 
                                onClick={() => handleDeleteTrigger(key, data[key])}  
                                size={20}   
                                className="ml-2"
                            />
                        </CustomListItem>
                    ))
                }
            </ol>
        </div>
    );
}

const FlexContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 25px;
`;

const CustomListItem = styled.li`
    font-size: 16px;
`;